import { Injectable } from '@angular/core';
import { addDoc, collection, doc, getDoc, getDocs, limit, orderBy, query, setDoc } from 'firebase/firestore';
import { AuthService } from './auth.service';
import { FirebaseService } from './firebase.service';
import { SITE_ACCESS_PAGES, SiteAccess, SiteAccessPage } from './site-access.logic';

export { SITE_ACCESS_PAGES };
export type { SiteAccess, SiteAccessPage };

/** One flip of a page's switch, kept in siteConfigLog. */
export interface AccessChange { pageId: string; from: SiteAccess; to: SiteAccess; by: string; at: string; }

/**
 * Admin switch for pages that can be open to everyone or kept for members (siteConfig/{pageId}.access).
 * "admin" means admins only, nobody else; "members" means users granted the page's feature key (allowedFeatureUsers) and admins. A page with no setting is public.
 */
@Injectable({ providedIn: 'root' })
export class SiteAccessService {
  private db: FirebaseService['db'];

  constructor(firebase: FirebaseService, private auth: AuthService) {
    this.db = firebase.db;
  }

  private cache = new Map<string, { at: number; value: SiteAccess }>();

  /** Same answer for a minute, so moving between pages doesn't re-read the setting every time. */
  async get(pageId: string): Promise<SiteAccess> {
    const hit = this.cache.get(pageId);
    if (hit && Date.now() - hit.at < 60_000) return hit.value;
    const value = await this.read(pageId);
    this.cache.set(pageId, { at: Date.now(), value });
    return value;
  }

  private allCache: { at: number; value: Record<string, SiteAccess> } | null = null;

  /** Every page's level in one read (the header uses it to hide the links of Admin only pages). */
  async levels(): Promise<Record<string, SiteAccess>> {
    if (this.allCache && Date.now() - this.allCache.at < 60_000) return this.allCache.value;
    const value: Record<string, SiteAccess> = {};
    for (const p of SITE_ACCESS_PAGES) value[p.id] = p.default;
    try {
      const snap = await getDocs(collection(this.db, 'siteConfig'));
      snap.docs.forEach(d => {
        const a = d.data()['access'];
        if (a === 'public' || a === 'members' || a === 'admin') value[d.id] = a;
      });
    } catch { /* unreadable: every page keeps its default */ }
    this.allCache = { at: Date.now(), value };
    return value;
  }

  /** The most recent switch changes, newest first. */
  async history(max = 20): Promise<AccessChange[]> {
    const snap = await getDocs(query(collection(this.db, 'siteConfigLog'), orderBy('at', 'desc'), limit(max)));
    return snap.docs.map(d => d.data() as AccessChange);
  }

  private async read(pageId: string): Promise<SiteAccess> {
    const fallback = SITE_ACCESS_PAGES.find(p => p.id === pageId)?.default ?? 'public';
    try {
      const snap = await getDoc(doc(this.db, 'siteConfig', pageId));
      const a = snap.exists() ? snap.data()['access'] : null;
      if (a === 'public' || a === 'members' || a === 'admin') return a;
    } catch { /* unreadable: use the page's default rather than guess */ }
    return fallback;
  }

  async set(pageId: string, access: SiteAccess): Promise<void> {
    const from = await this.read(pageId);
    const at = new Date().toISOString();
    const by = this.auth.currentUser?.email ?? this.auth.currentUser?.uid ?? '';
    await setDoc(doc(this.db, 'siteConfig', pageId), { access, updatedAt: at, updatedBy: by });
    try {
      await addDoc(collection(this.db, 'siteConfigLog'), { pageId, from, to: access, by, at });
    } catch (err) {
      console.error('Site access: could not write the change log', err);   // the change itself is saved
    }
    this.cache.delete(pageId);
    this.allCache = null;
  }
}
